import { HandleReturnItemChange } from 'components/Orders/Order';
import { DecisionCode, ReturnItem, ReturnOrderLine } from 'returnCenter-api';
import { useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { StyledSelect } from './RefundCell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/pro-solid-svg-icons';
import Flex from 'components/ui/Flex';
import { useTranslation } from 'react-i18next';

type IntercompanyDecisionCodeCellProps = {
    item: ReturnItem;
    itemId?: number | string;
    handleReturnItemChange: HandleReturnItemChange;
    orderline?: ReturnOrderLine;
    disabled?: boolean;

    /**
     * The supplying company's decision codes. Passed in rather than read from the store, which
     * holds the selling company's — picking from the wrong list is worse than an empty dropdown.
     */
    decisionCodes: DecisionCode[];

    /** What the supplying company's rules resolve to today, when the operator has not chosen. */
    suggestedDecisionCodeId?: number | null;
};

/**
 * The second decision on an MDC line: how the supplying company is credited, alongside the
 * DecisionCodeCell that says how the customer is credited. Both sit on the customer return so the
 * operator settles them in one place, before the intercompany slip exists.
 *
 * Kept close to DecisionCodeCell rather than shared with it: unlocking that one re-runs the rules
 * in the browser, while these rules run server-side when the slip is created.
 */
const IntercompanyDecisionCodeCell = ({
    item,
    itemId,
    handleReturnItemChange,
    orderline,
    disabled = false,
    decisionCodes,
    suggestedDecisionCodeId,
}: IntercompanyDecisionCodeCellProps) => {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);

    const isManual = item.intercompanyDecisionCodeIsManual === true;
    const isLocked = !isManual;

    const options = useMemo(() => {
        if (!decisionCodes || decisionCodes.length === 0) return [];
        return decisionCodes.map((code: DecisionCode) => ({
            value: code.id,
            label: code.description ?? `${code.id}`,
        }));
    }, [decisionCodes]);

    const currentValue = useMemo(() => {
        // Unlocked means the operator owns the field, so a changing reason code cannot move it.
        // Locked shows what the rules resolve to, which is not stored — the credit re-resolves it
        // at creation, and a stored copy could quietly disagree.
        const effective = isManual ? item.intercompanyDecisionCode : suggestedDecisionCodeId;
        if (effective === null || effective === undefined) return null;
        return options.find((option) => option.value === effective) ?? null;
    }, [options, isManual, item.intercompanyDecisionCode, suggestedDecisionCodeId]);

    const change = (property: 'intercompanyDecisionCode' | 'intercompanyDecisionCodeIsManual', value: number | boolean) =>
        handleReturnItemChange(
            orderline?.orderNumber ?? '',
            orderline?.sku ?? '',
            orderline?.orderLineNumber ?? '',
            itemId ?? -1,
            property,
            value,
        );

    return (
        <Wrapper $grow={1} align="center" gap="0.5rem">
            <StyledSelect
                isDisabled={disabled || isLocked}
                data-item-id={itemId}
                key={itemId}
                menuIsOpen={menuOpen}
                onMenuOpen={() => setMenuOpen(true)}
                onMenuClose={() => setMenuOpen(false)}
                defaultValue={null}
                value={currentValue}
                onChange={(selected: any) => {
                    change('intercompanyDecisionCode', (selected as { value: number }).value);
                    setMenuOpen(false);
                }}
                options={options}
                menuPortalTarget={document.body}
            />
            <LockButton
                onClick={() => change('intercompanyDecisionCodeIsManual', !isManual)}
                disabled={disabled}
                $isLocked={isLocked}
                type="button"
                title={
                    isLocked
                        ? t('intercompany.unlockToEdit', 'Unlock to edit manually')
                        : t('intercompany.lockToUseAutomatic', 'Lock to use automatic value')
                }
            >
                <FontAwesomeIcon icon={isLocked ? faLock : faLockOpen} />
            </LockButton>
        </Wrapper>
    );
};

export default IntercompanyDecisionCodeCell;

const Wrapper = styled(Flex)``;

const LockButton = styled.button<{ $isLocked: boolean }>`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    color: ${({ $isLocked, theme }) => ($isLocked ? theme.colors.textLight : theme.colors.accent)};
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
        opacity: 1;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.3;
    }
`;
